import { ArrowDownRight, ArrowUpRight, Info } from "lucide-react";
import { brl } from "@/lib/api";
import { Surface } from "@/components/ds";
import { BarsCompare, C, TrendLine } from "./charts";

const pct = (now, before) => {
  if (!before) return null;
  return Math.round(((now - before) / before) * 100);
};

const Kpi = ({ label, value, hint, delta, tone, testid }) => (
  <Surface className="p-4 sm:p-5" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</p>
    <p
      className={`mt-2 font-num text-xl font-semibold sm:text-2xl ${
        tone === "up" ? "text-emerald-300" : tone === "down" ? "text-red-300" : "text-foreground"
      }`}
    >
      {value}
    </p>
    {delta !== null && delta !== undefined && (
      <p
        className={`mt-1 flex items-center gap-1 text-[11px] ${
          delta >= 0 ? "text-emerald-300" : "text-red-300"
        }`}
      >
        {delta >= 0 ? (
          <ArrowUpRight className="h-3 w-3" />
        ) : (
          <ArrowDownRight className="h-3 w-3" />
        )}
        {Math.abs(delta)}% vs período anterior
      </p>
    )}
    {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
  </Surface>
);

export function FinanceOverview({ data }) {
  const k = data.kpis;
  const prev = data.previous;
  const has = prev.has_data;
  const maxBar = Math.max(k.sold, k.received, k.receivable, 1);
  const bar = (v) => `${Math.max((v / maxBar) * 100, 2)}%`;

  return (
    <div className="space-y-6" data-testid="finance-overview">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi
          label="Vendido"
          value={brl(k.sold)}
          delta={has ? pct(k.sold, prev.sold) : null}
          hint={`${k.orders} pedido(s)`}
          testid="kpi-sold"
        />
        <Kpi
          label="Recebido"
          value={brl(k.received)}
          tone="up"
          delta={has ? pct(k.received, prev.received) : null}
          hint={
            k.received_without_date > 0
              ? `${brl(k.received_without_date)} pago sem data registrada`
              : "na data da confirmação"
          }
          testid="kpi-received"
        />
        <Kpi
          label="A receber"
          value={brl(k.receivable)}
          hint={`clientes ${brl(k.receivable_customers)} · parceiros ${brl(k.receivable_partners)}`}
          testid="kpi-receivable"
        />
        <Kpi
          label="Despesas pagas"
          value={brl(k.expenses_paid)}
          tone="down"
          delta={has ? pct(k.expenses_paid, prev.expenses_paid) : null}
          hint={`a pagar ${brl(k.expenses_pending)}`}
          testid="kpi-expenses"
        />
        <Kpi
          label="Resultado de caixa"
          value={brl(k.cash_result)}
          tone={k.cash_result >= 0 ? "up" : "down"}
          hint="recebido − despesas pagas"
          testid="kpi-cash-result"
        />
        <Kpi
          label="Resultado operacional"
          value={k.operational_result === null ? "—" : brl(k.operational_result)}
          hint={
            k.operational_result === null
              ? "custo de produção não disponível no período"
              : `estimado · margem ${k.operational_margin ?? "—"}%`
          }
          testid="kpi-operational-result"
        />
        <Kpi label="Pedidos" value={k.orders} hint="no período" testid="kpi-orders" />
        <Kpi
          label="Ticket médio"
          value={brl(k.ticket)}
          delta={has ? pct(k.ticket, prev.ticket) : null}
          testid="kpi-ticket"
        />
      </div>

      <Surface className="p-5" data-testid="sold-received-block">
        <h2 className="text-base font-medium md:text-lg">Vendido × Recebido × A receber</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          São números diferentes: vender não é receber. Só o pagamento confirmado entra em
          Recebido.
        </p>
        <div className="mt-5 space-y-4">
          {[
            ["Vendido", k.sold, C.sold, "bar-sold"],
            ["Recebido", k.received, C.received, "bar-received"],
            ["A receber", k.receivable, C.cost, "bar-receivable"],
          ].map(([label, value, color, testid]) => (
            <div key={label} data-testid={testid}>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-num font-medium">{brl(value)}</span>
              </div>
              <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-white/5">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: bar(value), background: color }}
                />
              </div>
            </div>
          ))}
        </div>
      </Surface>

      <div className="grid gap-4 xl:grid-cols-2">
        <Surface className="p-5">
          <h3 className="text-sm font-medium">Vendido × Recebido por mês</h3>
          <p className="mb-3 text-xs text-muted-foreground">Últimos 6 meses</p>
          <BarsCompare
            data={data.series}
            bars={[
              { key: "sold", name: "Vendido", color: C.sold },
              { key: "received", name: "Recebido", color: C.received },
            ]}
            testid="chart-sold-received"
          />
        </Surface>
        <Surface className="p-5">
          <h3 className="text-sm font-medium">Evolução do faturamento e despesas</h3>
          <p className="mb-3 text-xs text-muted-foreground">Vendido, recebido e despesas pagas</p>
          <TrendLine
            data={data.series}
            lines={[
              { key: "sold", name: "Vendido", color: C.sold },
              { key: "received", name: "Recebido", color: C.received },
              { key: "expenses", name: "Despesas", color: C.expense },
            ]}
            testid="chart-evolution"
          />
        </Surface>
      </div>

      <Surface className="p-4" data-testid="methodology-note">
        <p className="flex gap-2 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand" />
          <span>
            <strong className="text-foreground">Como calculamos:</strong>{" "}
            {data.methodology.sold} {data.methodology.received} {data.methodology.receivable}{" "}
            <strong className="text-foreground">Resultado operacional:</strong>{" "}
            {data.methodology.operational_result}
          </span>
        </p>
      </Surface>
    </div>
  );
}
